import { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  useCreateApkUploadMutation,
  useGetAppVersionsQuery,
  usePublishAppVersionMutation,
  useDeleteAppVersionMutation,
} from '../../app/appVersionApi';
import './appVersions.css';
import { useAuth } from '../auth/AuthContext';
import { formatDashboardDateMedium } from '../../helpers/dashboardTime';

const initialForm = {
  appType: 'laucher', versionCode: '', version: '', releaseNotes: '', forceUpdate: false,
};

const messageFromError = (error) => (
  error?.data?.message || error?.error || error?.message || 'Request failed'
);

const formatDate = (value) => formatDashboardDateMedium(value, '—');

function ReleaseRow({ release, onDelete, deleting, canDelete }) {
  return (
    <tr>
      <td>
        <strong className="app-version-number">v{release.version}</strong>
        <span className="app-version-filename" title={release.fileName}>{release.fileName}</span>
      </td>
      <td className="app-version-build">{release.versionCode ?? '—'}</td>
      <td className="app-version-date">{formatDate(release.publishedAt)}</td>
      <td><span className={`release-status release-status--${release.status}`}>
        {release.status === 'published' ? 'Published' : 'Disabled'}
      </span></td>
      <td><span className={`release-status ${release.forceUpdate ? 'release-status--forced' : 'release-status--optional'}`}>
        {release.forceUpdate ? 'Required' : 'Optional'}
      </span></td>
      <td className="app-version-release-notes">{release.releaseNotes?.trim() || 'No release notes.'}</td>
      <td>{canDelete && <button type="button" className="version-button version-button--secondary" disabled={deleting} onClick={() => onDelete(release)}>Delete</button>}</td>
    </tr>
  );
}

export default function AppVersions() {
  const { isAdmin, hasRole } = useAuth();
  const canDelete = isAdmin() || hasRole('superadmin');
  const [deleteRelease, {isLoading:isDeleting}] = useDeleteAppVersionMutation();
  const remove = async release => {
    if (!window.confirm(`Delete ${release.appType || appType} v${release.version} (build ${release.versionCode ?? 'legacy'})?\nThis removes it from update checks. Clients may receive an older remaining release. The APK file will be retained.`)) return;
    try { await deleteRelease(release.id).unwrap(); toast.success('Version deleted'); }
    catch(error) { toast.error(messageFromError(error)); }
  };
  const [appType, setAppType] = useState('laucher');
  const [form, setForm] = useState(initialForm);
  const [apk, setApk] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const { currentData, isFetching, isError, refetch } = useGetAppVersionsQuery(appType);
  const [createUpload, { isLoading: isCreatingUpload }] = useCreateApkUploadMutation();
  const [publishRelease, { isLoading: isPublishing }] = usePublishAppVersionMutation();
  const releases = currentData?.data || [];
  const busy = isCreatingUpload || isPublishing || Boolean(uploadProgress);

  const updateField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const submit = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    if (!apk) return toast.error('Choose an APK file');
    if (!apk.name.toLowerCase().endsWith('.apk')) return toast.error('Only APK files are supported');

    try {
      setUploadProgress('Preparing upload…');
      const presign = await createUpload({ version: form.version, versionCode: Number(form.versionCode), appType: form.appType, fileName: apk.name }).unwrap();
      setUploadProgress('Uploading APK…');
      const upload = await fetch(presign.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/vnd.android.package-archive' },
        body: apk,
      });
      if (!upload.ok) throw new Error(`APK upload failed (${upload.status})`);

      setUploadProgress('Publishing version…');
      await publishRelease({
        ...form,
        versionCode: Number(form.versionCode),
        fileName: apk.name, storageKey: presign.data.key, uploadId: presign.data.uploadId,
      }).unwrap();
      setAppType(form.appType);
      setForm({ ...initialForm, appType: form.appType });
      setApk(null);
      formElement.reset();
      toast.success(`Android version ${form.version} published`);
    } catch (error) {
      toast.error(messageFromError(error));
    } finally {
      setUploadProgress('');
    }
  };

  return (
    <main className="app-versions-page">
      <div className="app-versions-heading">
        <div>
          <p className="app-versions-eyebrow">Android</p>
          <h1>App versions</h1>
          <p>Publish Android updates for Laucher and DHL.</p>
        </div>
        <button type="button" className="version-button version-button--secondary" onClick={refetch} disabled={isFetching}>Refresh</button>
      </div>

      <section className="app-version-card">
        <div className="app-version-section-heading"><h2>Publish a new version</h2><p>Release details cannot be edited after publishing.</p></div>
        <form className="app-version-form" onSubmit={submit}>
          <label>App type
            <select value={form.appType} disabled={busy} onChange={(e) => updateField('appType', e.target.value)}>
              <option value="laucher">Laucher</option><option value="dhl">DHL</option>
            </select>
          </label>
          <label>Build number
            <input required type="number" min="1" step="1" placeholder="38" value={form.versionCode} disabled={busy} onChange={(e) => updateField('versionCode', e.target.value)} />
          </label>
          <label>Version
            <input required placeholder="1.4.0" disabled={busy} value={form.version} onChange={(e) => updateField('version', e.target.value)} />
          </label>
          <label className="app-version-file">APK file
            <span className="app-version-file-picker">
              <input
                required
                className="app-version-file-input"
                type="file"
                disabled={busy}
                accept=".apk,application/vnd.android.package-archive"
                onChange={(e) => setApk(e.target.files?.[0] || null)}
              />
              <span className="app-version-file-button">Choose APK</span>
              <span className="app-version-file-name">{apk?.name || 'No file selected'}</span>
            </span>
          </label>
          <label className="app-version-notes">Release notes
            <textarea rows="3" placeholder="What changed in this version?" disabled={busy} value={form.releaseNotes} onChange={(e) => updateField('releaseNotes', e.target.value)} />
          </label>
          <div className="app-version-footer">
            <label className="app-version-checkbox">
              <input type="checkbox" disabled={busy} checked={form.forceUpdate} onChange={(e) => updateField('forceUpdate', e.target.checked)} />
              <span>Require update<small>Users must update before continuing.</small></span>
            </label>
            <div className="app-version-submit">
              {uploadProgress && <span role="status">{uploadProgress}</span>}
              <button className="version-button" type="submit" disabled={busy}>{busy ? 'Publishing…' : 'Upload and publish'}</button>
            </div>
          </div>
        </form>
      </section>

      <section className="app-version-list" aria-labelledby="release-history-heading">
        <div className="app-version-list__heading">
          <div><h2 id="release-history-heading">Release history</h2><p>Published release details are read-only. Administrators can delete releases.</p></div>
          <span className="app-version-count">{isFetching ? 'Loading…' : `${releases.length} ${releases.length === 1 ? 'version' : 'versions'}`}</span>
        </div>
        <div className="app-version-tabs" role="tablist" aria-label="App type">
          {['laucher', 'dhl'].map((type, index) => (
            <button key={type} type="button" role="tab" id={`release-tab-${type}`}
              aria-selected={appType === type} aria-controls="release-history-panel"
              tabIndex={appType === type ? 0 : -1}
              onClick={() => setAppType(type)}
              onKeyDown={(event) => {
                const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
                if (!keys.includes(event.key)) return;
                event.preventDefault();
                const next = event.key === 'Home' ? 'laucher' : event.key === 'End' ? 'dhl' : ['laucher', 'dhl'][1 - index];
                setAppType(next);
                document.getElementById(`release-tab-${next}`)?.focus();
              }}>
              {type === 'laucher' ? 'Laucher' : 'DHL'}
            </button>
          ))}
        </div>
        <div id="release-history-panel" role="tabpanel" aria-labelledby={`release-tab-${appType}`} aria-busy={isFetching}>
          {isFetching && <div className="app-version-empty" role="status">Loading versions…</div>}
          {!isFetching && isError && <div className="app-version-empty" role="alert">Unable to load versions. Please refresh to try again.</div>}
          {!isFetching && !isError && releases.length === 0 && <div className="app-version-empty">No {appType === 'laucher' ? 'Laucher' : 'DHL'} releases yet.</div>}
          {!isFetching && !isError && releases.length > 0 && (
            <div className="app-version-table-scroll">
              <table className="app-version-table">
                <thead><tr><th scope="col">Version / APK</th><th scope="col">Build</th><th scope="col">Published</th><th scope="col">Status</th><th scope="col">Update</th><th scope="col">Release notes</th><th scope="col">Actions</th></tr></thead>
                <tbody>{releases.map((release) => <ReleaseRow key={release.id} release={release} onDelete={remove} deleting={isDeleting} canDelete={canDelete} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
